import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Box, BOX_BRANDS, isValidIp } from '@/lib/api/boxes';

/** Editable subset of a box, shared by the Edit and Add dialogs. */
export type BoxFormState = {
  name: string;
  ip: string;
  brand: string;
  group: string;
  tv: string;
};

export function boxToForm(box?: Box): BoxFormState {
  return {
    name: box?.name ?? '',
    ip: box?.ip ?? '',
    brand: box?.brand ?? 'NVIDIA',
    group: box?.group ?? '',
    tv: box?.tv ?? '',
  };
}

/**
 * Validation shared by Edit and Add. `selfId` excludes the box being edited
 * from the uniqueness checks. Name is a hard key: the headend feed and its
 * remote-control groupings (KGroupingTv) join on it, so duplicates are
 * rejected outright. Duplicate IPs would make the agent poll one device twice
 * and make the IP-keyed alertBoxes/liveBoxes docs ambiguous.
 */
export function boxFormErrors(
  form: BoxFormState,
  boxes: Box[] | undefined,
  selfId?: string
): { name: string; ip: string } {
  const name = form.name.trim();
  const ip = form.ip.trim();
  const others = (boxes ?? []).filter((b) => b.id !== selfId);
  return {
    name: !name
      ? 'Name is required'
      : others.some((b) => b.name === name)
        ? 'Another box already uses this name'
        : '',
    ip: !ip
      ? 'IP is required'
      : !isValidIp(ip)
        ? 'Enter a valid IPv4 address'
        : others.some((b) => b.ip === ip)
          ? 'Another box already uses this IP'
          : '',
  };
}

type Props = {
  form: BoxFormState;
  onChange: (next: BoxFormState) => void;
  errors: { name: string; ip: string };
  /** Keeps input ids unique when several dialogs are mounted. */
  idPrefix: string;
  /** Shown under the fields only when that field differs from its original. */
  nameChanged?: boolean;
  ipChanged?: boolean;
};

export function BoxFormFields({
  form,
  onChange,
  errors,
  idPrefix,
  nameChanged,
  ipChanged,
}: Props) {
  const set = (patch: Partial<BoxFormState>) => onChange({ ...form, ...patch });
  return (
    <>
      <div className="grid gap-2 py-2">
        <label className="text-sm font-medium" htmlFor={`${idPrefix}-name`}>
          Name
        </label>
        <Input
          id={`${idPrefix}-name`}
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="NE Field Box 1 TV-1"
          aria-invalid={!!errors.name}
        />
        {errors.name ? (
          <p className="text-sm text-destructive">{errors.name}</p>
        ) : (
          nameChanged && (
            <p className="text-sm text-muted-foreground">
              The headend and remote-control groupings reference boxes by name —
              update them after a rename.
            </p>
          )
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 py-2">
        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor={`${idPrefix}-brand`}>
            Type
          </label>
          <Select value={form.brand} onValueChange={(v) => set({ brand: v })}>
            <SelectTrigger id={`${idPrefix}-brand`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BOX_BRANDS.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor={`${idPrefix}-ip`}>
            IP address
          </label>
          <Input
            id={`${idPrefix}-ip`}
            value={form.ip}
            onChange={(e) => set({ ip: e.target.value })}
            placeholder="10.106.138.10"
            aria-invalid={!!errors.ip}
          />
        </div>
      </div>
      {errors.ip ? (
        <p className="text-sm text-destructive">{errors.ip}</p>
      ) : (
        ipChanged && (
          <p className="text-sm text-muted-foreground">
            The agent polls this address directly — a wrong value shows the box
            as offline until it&apos;s corrected.
          </p>
        )
      )}

      <div className="grid grid-cols-2 gap-4 py-2">
        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor={`${idPrefix}-group`}>
            Group
          </label>
          <Input
            id={`${idPrefix}-group`}
            value={form.group}
            onChange={(e) => set({ group: e.target.value })}
            placeholder="HDA"
          />
        </div>
        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor={`${idPrefix}-tv`}>
            TV
          </label>
          <Input
            id={`${idPrefix}-tv`}
            value={form.tv}
            onChange={(e) => set({ tv: e.target.value })}
            placeholder="TV-1"
          />
        </div>
      </div>
    </>
  );
}
